'use client';

import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeftIcon, LayoutIcon, ListDashesIcon, FileArrowDownIcon, PlusIcon, GridFourIcon, CalendarDotsIcon, FileTextIcon, NotePencilIcon, PresentationIcon, TextAaIcon, BookmarksSimpleIcon } from '@phosphor-icons/react';
import { Tooltip } from '@heroui/tooltip';
import { View } from '@/shared/types';
import { FetchWithVersionCheck, getCaseDisplayName } from '@/shared/functions';
import { useToolsDrawer } from './tools-drawer';
const getDocxName = (caseId: string) => {
  const caseIdNice = caseId.trim().toLowerCase().replace(/([^A-Z0-9]+)(.)/ig, function (match) {
    return match[1]?.toUpperCase();
  }) || '';
  return `${caseIdNice}_ThreatReport.docx`;
};
interface WorkProductNavBarProps {
  featureFlags?: {
    [key: string]: any;
  };
  onBackToWorkProducts?: () => void;
  onViewChange?: (view: View) => void;
  selectedView?: View;
  caseId?: string;
  workProductType?: 'timeline' | 'threat-report';
  isIndexView?: boolean;
  onCreateTimeline?: () => void;
  onCreateThreatReport?: () => void;
  viewMode?: 'grid' | 'list';
  onViewModeChange?: (mode: 'grid' | 'list') => void;
  timelineTitle?: string;
  onEditTimelineTitle?: () => void;
  isEditableTimeline?: boolean;
}
const WorkProductNavBar: React.FC<WorkProductNavBarProps> = ({
  featureFlags,
  onBackToWorkProducts,
  onViewChange,
  selectedView,
  caseId,
  workProductType = 'timeline',
  isIndexView = false,
  onCreateTimeline,
  onCreateThreatReport,
  viewMode = 'grid',
  onViewModeChange,
  timelineTitle,
  onEditTimelineTitle,
  isEditableTimeline
}) => {
  const caseDisplayName = getCaseDisplayName(caseId);
  const toolsDrawer = useToolsDrawer();
  const isChatView = selectedView === 'chat';
  let title = 'Work Products';
  if (isChatView) {
    title = 'Assistant';
  } else if (!isIndexView) {
    if (workProductType === 'threat-report') {
      title = 'Threat Report';
    } else if (workProductType === 'timeline' && timelineTitle) {
      title = timelineTitle;
    } else {
      title = 'Timeline';
    }
  }
  const [isCreateDropdownOpen, setIsCreateDropdownOpen] = useState(false);
  const createDropdownRef = useRef<HTMLDivElement>(null);
  const handleDownloadDocx = async (caseId: string) => {
    let html = document.getElementById('threat-report-docx')?.innerHTML;
    if (!html || html.trim() === '') {
      console.error('No threat report HTML found');
      return;
    }
    const response = await FetchWithVersionCheck(`/api/protected/threatReport/${encodeURIComponent(caseId)}`, {
      method: 'POST',
      body: JSON.stringify({
        action: 'exportDocX',
        html: html
      })
    });
    if (!response.ok) {
      console.error('Failed to download DOCX');
      return;
    }
    const docxName = getDocxName(caseId);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = docxName;
    a.click();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (createDropdownRef.current && !createDropdownRef.current.contains(event.target as Node)) {
        setIsCreateDropdownOpen(false);
      }
    };
    if (isCreateDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCreateDropdownOpen]);
  const handleCreateTimeline = () => {
    if (onCreateTimeline) {
      onCreateTimeline();
    }
    setIsCreateDropdownOpen(false);
  };
  const handleCreateThreatReport = () => {
    if (onCreateThreatReport) {
      onCreateThreatReport();
    }
    setIsCreateDropdownOpen(false);
  };
  if (!featureFlags?.workProductNav) {
    return null;
  }
  if (!caseId && !isIndexView) {
    return null;
  }
  return <nav id="work-product-nav-bar" role="navigation" aria-label="Work products navigation" className="fixed top-[50px] sm:top-[67px] inset-x-0 z-40 h-auto sm:h-[80px] flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 sm:px-5 py-2 sm:py-3 border-b border-white/[0.06] box-border" style={{
    backgroundColor: '#060F1B'
  }} data-sentry-component="WorkProductNavBar" data-sentry-source-file="work-product-nav-bar.tsx">
      <div className="flex flex-row items-center gap-3 min-w-0 flex-1 w-full sm:w-auto">
        {!isIndexView && !isChatView && onBackToWorkProducts && <button onClick={onBackToWorkProducts} className="flex-shrink-0 p-2 text-white hover:bg-white/10 rounded-lg transition-colors focus:outline focus:outline-2 focus:outline-white" aria-label="Back to work products">
            <ArrowLeftIcon size={20} />
          </button>}
        <div className="flex flex-col min-w-0 flex-1">
          <div className="flex flex-row items-center gap-2 min-w-0">
            <span className="text-2xl font-bold text-white truncate max-w-[200px] sm:max-w-[400px] md:max-w-[600px]">{title}</span>
            {workProductType === 'timeline' && isEditableTimeline && onEditTimelineTitle && <Tooltip className="bg-gray-800 border-2 border-gray-200 rounded-lg text-white p-2 w-[200px] text-center" content="Edit timeline title slide." placement="bottom" delay={0}>
                <button data-testid="EditTitleSlideButton" className="flex-shrink-0 p-1.5 text-white hover:bg-white/10 rounded-lg transition-colors" onClick={onEditTimelineTitle}>
                  <NotePencilIcon weight="duotone" className="w-[20px] h-[20px]" />
                </button>
              </Tooltip>}
          </div>
          {caseDisplayName && <span className="text-sm italic text-white/65 truncate">Case: {caseDisplayName}</span>}
        </div>
        {toolsDrawer && <div className="flex-shrink-0 ml-auto sm:hidden">
            <Tooltip content="Favorites" placement="bottom" showArrow={true} closeDelay={100} classNames={{
          base: 'before:bg-tooltip-bg z-[99999999]',
          content: 'bg-tooltip-bg text-white rounded-sm p-2 max-w-[240px]'
        }}>
              <button type="button" aria-label="Favorites" onClick={() => toolsDrawer.toggleDrawer()} className={`flex items-center justify-center rounded-md hover:bg-white/10 w-[28px] h-[28px] cursor-pointer p-1 transition-colors ${toolsDrawer.isOpen ? 'bg-white/20 ring-1 ring-white/40' : ''}`}>
                <BookmarksSimpleIcon size={22} weight={toolsDrawer.isOpen ? 'fill' : 'regular'} className="text-white" />
              </button>
            </Tooltip>
          </div>}
      </div>
      <div className="flex flex-row items-center gap-4 flex-shrink-0 ml-0 sm:ml-4 mt-2 sm:mt-0 w-full sm:w-auto">
        {isChatView ? null : isIndexView ? <>
            {onViewModeChange && <div className="flex flex-row items-center gap-1 bg-white/5 rounded-md p-1">
                <button tabIndex={0} aria-label="Switch to grid view" onClick={() => onViewModeChange('grid')} className={`btn-text gap-2 ${viewMode === 'grid' ? '' : 'bg-transparent border-white/20 text-white/65 hover:text-white/75 hover:bg-white/5'}`}>
                  <GridFourIcon size={20} />
                  <span>Grid</span>
                </button>
                <button tabIndex={0} aria-label="Switch to list view" onClick={() => onViewModeChange('list')} className={`btn-text gap-2 ${viewMode === 'list' ? '' : 'bg-transparent border-white/20 text-white/65 hover:text-white/75 hover:bg-white/5'}`}>
                  <ListDashesIcon size={20} />
                  <span>List</span>
                </button>
              </div>}
            {onCreateTimeline && <div ref={createDropdownRef} className="relative">
                <button tabIndex={0} aria-label="Create new work product" aria-haspopup="menu" aria-expanded={isCreateDropdownOpen} onClick={() => setIsCreateDropdownOpen(!isCreateDropdownOpen)} className="btn-text gap-2">
                  <PlusIcon size={20} />
                  <span>Create New</span>
                </button>
                {isCreateDropdownOpen && <div className="absolute top-full right-0 mt-2 bg-[#2C3A4A] rounded-md shadow-lg border border-white/10 z-[1001] min-w-[160px] overflow-hidden" onClick={e => e.stopPropagation()}>
                    <button tabIndex={0} aria-label="Create new timeline" onClick={handleCreateTimeline} className={`w-full px-4 py-2 text-left text-white text-sm hover:bg-white/10 cursor-pointer transition-colors flex flex-row items-center gap-2 ${featureFlags?.threatReports ? 'first:rounded-t-md' : 'rounded-md'}`}>
                      <CalendarDotsIcon size={16} weight="regular" />
                      <span>Timeline</span>
                    </button>
                    {featureFlags?.threatReports && <button tabIndex={0} aria-label="Create new threat report" onClick={handleCreateThreatReport} className="w-full px-4 py-2 text-left text-white text-sm hover:bg-white/10 last:rounded-b-md cursor-pointer transition-colors flex flex-row items-center gap-2">
                        <FileTextIcon size={16} weight="regular" />
                        <span>Threat Report</span>
                      </button>}
                  </div>}
              </div>}
          </> : <>
            {workProductType === 'timeline' && onViewChange && <div className="flex flex-row items-center gap-1 bg-white/5 rounded-md p-1">
                <button onClick={() => onViewChange('table')} className={`btn-text gap-2 ${selectedView === 'table' ? '' : 'bg-transparent border-white/20 text-white/65 hover:text-white/75 hover:bg-white/5'}`}>
                  <ListDashesIcon size={20} />
                  <span>Details</span>
                </button>
                <button onClick={() => onViewChange('presentation')} className={`btn-text gap-2 ${selectedView === 'presentation' ? '' : 'bg-transparent border-white/20 text-white/65 hover:text-white/75 hover:bg-white/5'}`}>
                  <LayoutIcon size={20} />
                  <span>Presentation</span>
                </button>
                {featureFlags?.courtroomMode && <button onClick={() => onViewChange('courtroom')} className={`btn-text gap-2 ${selectedView === 'courtroom' ? '' : 'bg-transparent border-white/20 text-white/50 hover:text-white/75 hover:bg-white/5'}`}>
                    <PresentationIcon size={20} />
                    <span>Courtroom</span>
                  </button>}
              </div>}
            {workProductType === 'threat-report' && <button onClick={() => {
          if (caseId) {
            handleDownloadDocx(caseId);
          }
        }} className="btn-text gap-2" disabled={!caseId}>
                <FileArrowDownIcon size={20} />
                <span>Export DOCX</span>
              </button>}
          </>}
      </div>
      {toolsDrawer && <div className="hidden sm:flex flex-shrink-0 ml-4">
          <Tooltip content="Favorites" placement="bottom" showArrow={true} closeDelay={100} classNames={{
        base: 'before:bg-tooltip-bg z-[99999999]',
        content: 'bg-tooltip-bg text-white rounded-sm p-2 max-w-[240px]'
      }}>
            <button type="button" aria-label="Favorites" onClick={() => toolsDrawer.toggleDrawer()} className={`flex items-center justify-center rounded-md hover:bg-white/10 w-[28px] h-[28px] cursor-pointer p-1 transition-colors ${toolsDrawer.isOpen ? 'bg-white/20 ring-1 ring-white/40' : ''}`}>
              <BookmarksSimpleIcon size={22} weight={toolsDrawer.isOpen ? 'fill' : 'regular'} className="text-white" />
            </button>
          </Tooltip>
        </div>}
    </nav>;
};
export default WorkProductNavBar;